import type { PortableTextComponents, PortableTextMarkComponent } from "@portabletext/react";

type LinkMark = {
  _type: "link";
  href?: string;
};

type FileLinkMark = {
  _type: "fileLink";
  url?: string;
  originalFilename?: string;
};

const SAFE_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

// Mirrors the schema's scheme validation, which only runs in the Studio. Browsers drop
// control characters and whitespace before reading a scheme, so they are stripped first.
function isSafeHref(href: string): boolean {
  // eslint-disable-next-line no-control-regex
  const normalised = href.replace(/[\u0000-\u0020]/g, "");
  const scheme = /^[a-z][a-z0-9+.-]*:/i.exec(normalised)?.[0].toLowerCase();
  return normalised !== "" && (scheme === undefined || SAFE_SCHEMES.has(scheme));
}

// Declared explicitly rather than relying on @portabletext/react's default, to match the schema's `href` field.
const Link: PortableTextMarkComponent<LinkMark> = ({ value, children }) => {
  if (value?.href == null || !isSafeHref(value.href))
    return <>{children}</>;

  return <a href={value.href}>{children}</a>;
};

// Opens a document uploaded to Sanity. `url` is resolved from the file asset by the site config query.
const FileLink: PortableTextMarkComponent<FileLinkMark> = ({ value, children }) => {
  if (value?.url == null || value.url === "")
    return <>{children}</>;

  return (
    <a href={value.url} target="_blank" rel="noopener noreferrer" title={value.originalFilename}>
      {children}
    </a>
  );
};

export const usefulLinksComponents: PortableTextComponents = {
  marks: {
    link: Link,
    fileLink: FileLink,
  },
};
